import { useEffect } from 'react';
import { useCallStore } from '../stores/call-store';
import { apiClient, type Call } from '../services/api';

export function CallHistory() {
  const { user, callHistory, setCallHistory } = useCallStore();

  useEffect(() => {
    if (!user) return;

    const loadHistory = async () => {
      try {
        const history = await apiClient.getCallHistory(user.id, 50);
        // Ensure history is an array
        if (Array.isArray(history)) {
          setCallHistory(history);
        } else {
          console.warn('Call history is not an array:', history);
          setCallHistory([]);
        }
      } catch (error) {
        console.error('Failed to load call history:', error);
        setCallHistory([]);
      }
    };

    loadHistory();
  }, [user, setCallHistory]);

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Determine if call is inbound or outbound relative to the current user
  const isInboundForUser = (call: Call): boolean => {
    if (!user) return call.direction === 'inbound';

    // Check if user's phone number or SIP username matches the to_number (they received the call)
    const userIdentifiers = [
      user.phone_number,
      user.sip_username,
      // Also check common SIP extensions like "1000", "1001" etc.
    ].filter(Boolean).map(id => id?.toLowerCase());

    const toNumber = call.to_number?.toLowerCase() || '';
    const fromNumber = call.from_number?.toLowerCase() || '';

    // If user matches to_number, it's inbound for them
    if (userIdentifiers.some(id => toNumber.includes(id!) || id!.includes(toNumber))) {
      return true;
    }
    // If user matches from_number, it's outbound for them
    if (userIdentifiers.some(id => fromNumber.includes(id!) || id!.includes(fromNumber))) {
      return false;
    }
    // Fallback to the call's stored direction
    return call.direction === 'inbound';
  };

  const getCallIcon = (call: Call): string => {
    // Arrow indicates direction relative to user: ↙ incoming, ↗ outgoing
    if (isInboundForUser(call)) {
      return '↙️';
    }
    return '↗️';
  };

  const getDirectionLabel = (call: Call): string => {
    return isInboundForUser(call) ? 'Inbound' : 'Outbound';
  };

  // Get the other party's number (the one the user communicated with)
  const getOtherPartyNumber = (call: Call): string => {
    if (isInboundForUser(call)) {
      return call.from_number || 'Unknown';
    }
    return call.to_number || 'Unknown';
  };

  const getCallClass = (call: Call): string => {
    const direction = isInboundForUser(call) ? 'inbound' : 'outbound';
    const classes = ['call-history-item', `call-${direction}`];
    if (call.status === 'failed' || call.status === 'busy') {
      classes.push('call-failed');
    }
    return classes.join(' ');
  };

  // Safety check - ensure callHistory is an array
  const historyArray = Array.isArray(callHistory) ? callHistory : [];

  if (historyArray.length === 0) {
    return (
      <div className="call-history empty">
        <p>No call history yet</p>
      </div>
    );
  }

  return (
    <div className="call-history">
      <h3>Call History</h3>
      <div className="call-history-list">
        {historyArray.map((call) => (
          <div key={call.id} className={getCallClass(call)}>
            <div className="call-icon">{getCallIcon(call)}</div>
            <div className="call-details">
              <div className="call-number">
                {getOtherPartyNumber(call)}
              </div>
              <div className="call-meta">
                <span className={`call-direction direction-${isInboundForUser(call) ? 'inbound' : 'outbound'}`}>
                  {getDirectionLabel(call)}
                </span>
                <span className="call-time">
                  {formatTimestamp(call.start_time)}
                </span>
                {call.duration !== undefined && call.duration > 0 && (
                  <span className="call-duration">
                    {formatDuration(call.duration)}
                  </span>
                )}
              </div>
            </div>
            <div className="call-status">
              <span className={`status-badge status-${call.status}`}>
                {call.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
